import type {
  DayType,
  MealItem,
  MealSlot,
  NutritionGoal,
  NutritionTarget,
  PlannedMeal,
  TrainingSession,
  TrainingTimeOfDay,
} from '@cadencia/contracts';
import {
  MissingProfileDataError,
  STEPS_FOR_NEAT,
  adjustDay,
  calibrateTdee,
  dayTypeFor,
  energyTargetKcal,
  estimateRmr,
  estimateTdeeByComponents,
  generateWeeklyMenu,
  isSignificantLowerBody,
  itemsMacros,
  resolveTrainingTimeOfDay,
  roundMacros,
  stepsForNeat,
  validateMealSlots,
  weeklyTargets,
  weightTrend,
  type DayRequest,
  type StepsForNeat,
  type LoggedMealInput,
  type MacroTargets,
  type PendingMealInput,
  type WeeklyMenu,
} from '@cadencia/domain';
import { addDays, startOfWeek, todayLocal } from '../../lib/dates.js';
import { logger } from '../../lib/logger.js';
import { listActivity } from '../repositories/activityRepo.js';
import { listCardioOfWeek } from '../repositories/cardioRepo.js';
import {
  getPlanOfWeek,
  listMealLogsBetween,
  listMealLogsOfDay,
  listPlannedMealsBetween,
  listPlannedMealsOfDay,
  logMeal,
  savePlanWithMeals,
  unlogMeal,
  updatePlannedMeal,
  type PlannedMealInput,
} from '../repositories/mealPlanRepo.js';
import { favoriteSlugs, getTargetForDate, listTargetsBetween, markRecipesUsed, recipeHistory, saveTarget } from '../repositories/nutritionRepo.js';
import { latestWeight, weightPoints } from '../repositories/progressRepo.js';
import { getPreferences, getProfile } from '../repositories/profileRepo.js';
import {
  blocksOverlapping,
  listSessionsOfDateRange,
  setSessionTrainingTime as escribirTurnoDeSesion,
  weekSessions,
} from '../repositories/trainingRepo.js';
import { leerComidas } from '../preferenciaComidas.js';
import { savePreferences } from '../repositories/profileRepo.js';
import type { WriteContext } from '../writes.js';

/**
 * Nutricion: de los datos del usuario a un menu que cierra el dia.
 *
 * El motor esta entero en `@cadencia/domain` y no sabe nada de Dexie. Este
 * servicio es el unico sitio donde se decide QUE datos alimentan al motor, y
 * por eso es tambien donde se explican las suposiciones que se hacen cuando un
 * dato no existe.
 */

export interface PasosParaElGasto extends StepsForNeat {
  /** Primer y ultimo dia de la ventana mirada, ambos incluidos. */
  desde: string;
  hasta: string;
}

/**
 * Los pasos con los que se estima el NEAT del §2.2, y de donde salen.
 *
 * Hasta el 13/09/2026 eran 6.000 para todo el mundo (`neat_assumed_default`):
 * la V1 no integra pulseras —decision expresa del usuario— y no habia donde
 * anotarlos a mano. Ahora se anotan en `Progreso → Peso` y, con dias
 * suficientes, mandan ellos; si no, se sigue suponiendo y se sigue diciendo.
 *
 * La ventana mira hacia atras desde `today`, igual que la calibracion: los
 * pasos son un habito que se mide en lo vivido, tambien cuando se planifica una
 * semana futura. Termina AYER (`STEPS_FOR_NEAT`). La pantalla usa esta misma
 * funcion para decir que numero esta usando el gasto.
 */
export async function pasosParaElGasto(
  userId: string,
  today = todayLocal(),
): Promise<PasosParaElGasto> {
  const hasta = addDays(today, -1);
  const desde = addDays(today, -STEPS_FOR_NEAT.windowDays);
  const filas = await listActivity(userId, desde, hasta);
  return { ...stepsForNeat(filas.map((fila) => fila.steps)), desde, hasta };
}

/** Dias de historia de peso e ingesta que mira la calibracion (§2.3). */
const CALIBRATION_WINDOW_DAYS = 21;

export interface NutritionContext {
  bodyWeightKg: number;
  bodyFatPct: number | null;
  rmrKcal: number;
  tdeeKcal: number;
  tdeeSource: 'formula' | 'adaptive';
  reasonCodes: string[];
}

export class MissingNutritionDataError extends Error {
  constructor(readonly missing: string) {
    super(`Falta un dato para calcular la nutricion: ${missing}`);
    this.name = 'MissingNutritionDataError';
  }
}

function edadEnAnios(birthDate: string | null, today: string): number | null {
  if (birthDate === null) return null;
  const nacimiento = new Date(`${birthDate}T00:00:00`);
  const hoy = new Date(`${today}T00:00:00`);
  let anios = hoy.getFullYear() - nacimiento.getFullYear();
  const mes = hoy.getMonth() - nacimiento.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) anios -= 1;
  return anios;
}

export interface ContextoBasal {
  bodyWeightKg: number;
  bodyFatPct: number | null;
  rmrKcal: number;
  reasonCodes: string[];
}

/**
 * Lo que no depende de ninguna semana: peso, composición y RMR.
 *
 * Existe por `readjustDay` (NEW-8). Esa función pedía un `nutritionContext`
 * entero —las sesiones y el cardio de la semana, 21 días de registros de
 * comidas, los objetivos de esos días, todas las pesadas y la calibración
 * adaptativa— para quedarse solo con el peso y el RMR, y eso **después de cada
 * comida registrada**, que es lo que más se repite en la aplicación. El objetivo
 * del día no se recalcula al reajustar: se lee congelado. Así que ese gasto no
 * cambiaba nada y se pagaba siempre.
 *
 * Lanza lo mismo que lanzaba `nutritionContext` por esta parte: sin perfil o sin
 * peso no hay RMR, y un número inventado sería peor que pedir el dato.
 */
export async function contextoBasal(
  userId: string,
  today = todayLocal(),
): Promise<ContextoBasal> {
  const perfil = await getProfile(userId);
  if (perfil === undefined) throw new MissingNutritionDataError('perfil');

  const peso = await latestWeight(userId);
  if (peso === undefined) throw new MissingNutritionDataError('peso corporal');

  const reasonCodes: string[] = [];

  let rmr: number;
  try {
    const estimacion = estimateRmr({
      weightKg: peso.weightKg,
      heightCm: perfil.heightCm,
      bodyFatPct: peso.bodyFatPct,
      ageYears: edadEnAnios(perfil.birthDate, today),
      sex: perfil.sex,
    });
    rmr = estimacion.kcal;
    reasonCodes.push(`rmr_${estimacion.method}`);
    if (estimacion.lowConfidence) reasonCodes.push('rmr_low_confidence');
  } catch (caught) {
    if (caught instanceof MissingProfileDataError) {
      throw new MissingNutritionDataError(caught.missing);
    }
    throw caught;
  }

  return {
    bodyWeightKg: peso.weightKg,
    bodyFatPct: peso.bodyFatPct,
    rmrKcal: rmr,
    reasonCodes,
  };
}

export interface NutritionContextOptions {
  /**
   * La semana que se esta PLANIFICANDO, si no es la de `today`.
   *
   * Solo afecta al gasto por componentes: el §2.2 lo calcula con las sesiones
   * y el cardio de una semana concreta, y la semana que hay que medir es la que
   * se va a comer, no la que se esta viviendo.
   */
  forWeekStart?: string;
}

/**
 * Reune lo que el motor de energia necesita: peso, composicion, gasto.
 *
 * Lanza si falta el peso. No hay estimacion razonable de gasto energetico sin
 * saber cuanto pesa alguien, y devolver un numero inventado seria peor que
 * pedir que se pese: todo lo demas —objetivo, macros, menu— cuelga de ahi.
 *
 * `today` Y `forWeekStart` SON DOS COSAS DISTINTAS, Y ESTUVIERON JUNTAS
 *
 * `today` significa **cuando se esta calculando**: de ahi salen la edad para el
 * RMR y la ventana de 21 dias de la calibracion adaptativa, que mira hacia
 * atras y por tanto no puede terminar en el futuro. `forWeekStart` significa
 * **que semana se esta planificando**: de ahi sale el entrenamiento con el que
 * el §2.2 estima el gasto.
 *
 * Hasta el 12/09/2026 era un solo parametro, y los dos llamadores no lo
 * entendian igual: la pantalla de nutricion mandaba el DIA QUE SE ESTA MIRANDO
 * y `regenerateFutureMenus` el de verdad. Planificando una semana futura —que
 * es justo lo que permite el navegador de dias— los dos estimaban un TDEE
 * distinto y escribian **dos objetivos distintos para la misma fecha**: medido,
 * 299,9 g de hidratos contra 295,2. No fallaba nada; salian dos numeros
 * plausibles y ganaba el ultimo que escribiera.
 */
export async function nutritionContext(
  userId: string,
  today = todayLocal(),
  opciones: NutritionContextOptions = {},
): Promise<NutritionContext> {
  // Peso, composición y RMR salen de `contextoBasal`: es la misma cuenta, en un
  // solo sitio, que `readjustDay` usa sin arrastrar el gasto (NEW-8).
  const basal = await contextoBasal(userId, today);
  const peso = { weightKg: basal.bodyWeightKg, bodyFatPct: basal.bodyFatPct };
  const rmr = basal.rmrKcal;
  const reasonCodes: string[] = [...basal.reasonCodes];

  // Actividad real de la semana que se planifica, no una declaracion generica.
  const semanaMedida = startOfWeek(opciones.forWeekStart ?? today);
  const semana = await semanaDe(userId, semanaMedida);
  if (semanaMedida !== startOfWeek(today)) {
    // Queda dicho en la fila: el gasto de ese dia se estimo con el
    // entrenamiento de OTRA semana, que es lo correcto y no es obvio.
    reasonCodes.push('tdee_from_week_planned');
  }
  const sesiones = semana.sesiones.filter((sesion) => sesion.sessionKind === 'strength');
  const minutosMedios =
    sesiones.length === 0
      ? 0
      : Math.round(
          sesiones.reduce((total, sesion) => total + sesion.estimatedMinutes, 0) / sesiones.length,
        );
  const kmSemana = semana.cardio.reduce(
    (total, carrera) => total + (carrera.targetDistanceKm ?? carrera.targetMinutes / 6),
    0,
  );

  const pasos = await pasosParaElGasto(userId, today);
  const formula = estimateTdeeByComponents({
    rmrKcal: rmr,
    bodyWeightKg: peso.weightKg,
    dailySteps: pasos.steps,
    strengthSessionsPerWeek: sesiones.length,
    strengthMinutesPerSession: minutosMedios,
    runKmPerWeek: kmSemana,
  });
  // Uno de los dos, siempre: el objetivo congelado dice si su NEAT se midio.
  reasonCodes.push(pasos.source === 'logged' ? 'neat_from_logged_steps' : 'neat_assumed_default');

  const calibracion = await calibracionDeTdee(userId, formula.kcal, today);
  reasonCodes.push(...calibracion.reasonCodes);

  return {
    bodyWeightKg: peso.weightKg,
    bodyFatPct: peso.bodyFatPct,
    rmrKcal: rmr,
    tdeeKcal: calibracion.tdeeKcal,
    tdeeSource: calibracion.source,
    reasonCodes,
  };
}

/**
 * Sesiones y cardio de una semana. Recibe el LUNES ya resuelto.
 *
 * Se llamaba `semanaActual` y tomaba un dia cualquiera, lo que la hacia parecer
 * «la semana en curso» viniera de donde viniera el dia. Quien decide que semana
 * se mide es ahora `nutritionContext`, en un solo sitio y con el nombre delante.
 */
async function semanaDe(
  userId: string,
  lunes: string,
): Promise<{
  sesiones: Awaited<ReturnType<typeof weekSessions>>;
  cardio: Awaited<ReturnType<typeof listCardioOfWeek>>;
}> {
  return {
    sesiones: await weekSessions(userId, lunes),
    cardio: await listCardioOfWeek(userId, lunes),
  };
}

/**
 * Cuarta condicion de validez del §2.3: «sin cambio de objetivo en la ventana».
 *
 * El objetivo nutricional decide el ajuste energetico (`NUTRITION_GOAL_ENERGY`),
 * asi que un TDEE observado a caballo entre dos objetivos mezcla dos lineas
 * base distintas y atribuye al metabolismo lo que fue un cambio de plan.
 *
 * Hasta el 21/09/2026 esto miraba la FASE del bloque, que era quien decidia la
 * energia. Desde la recuperacion (28/09/2026) la decide el usuario, y el
 * objetivo no se historiza aparte: cada fila de `nutrition_targets` lleva en
 * `reasonCodes` el `goal_*` con el que se calculo (`energyTargetKcal`). Dos
 * distintos en la ventana son un cambio.
 *
 * Sigue contando tambien que un bloque de entrenamiento haya EMPEZADO dentro de
 * la ventana: un bloque nuevo reinicia la rampa de volumen, y el gasto se
 * mueve con ella. Es la nutricion leyendo el entrenamiento, que es el sentido
 * permitido. Ante la duda no se recalibra, que es el lado seguro.
 */
async function huboCambioEnLaVentana(
  userId: string,
  objetivos: readonly NutritionTarget[],
  desde: string,
  hasta: string,
): Promise<boolean> {
  const objetivosVistos = new Set(
    objetivos.flatMap((objetivo) => objetivo.reasonCodes.filter((c) => c.startsWith('goal_'))),
  );
  if (objetivosVistos.size > 1) return true;
  const bloques = await blocksOverlapping(userId, desde, hasta);
  return bloques.some((bloque) => bloque.startedOn > desde);
}

/**
 * Calibracion adaptativa contra lo comido y lo que ha hecho el peso.
 *
 * Si no hay datos suficientes devuelve el numero de la formula CON su motivo,
 * que es lo que pide el §2.3: un TDEE «adaptado» con cuatro dias de registro
 * seria peor que la formula y tendria pinta de estar medido.
 */
async function calibracionDeTdee(
  userId: string,
  formulaKcal: number,
  today: string,
): Promise<{ tdeeKcal: number; source: 'formula' | 'adaptive'; reasonCodes: string[] }> {
  const desde = addDays(today, -CALIBRATION_WINDOW_DAYS);
  const registros = await listMealLogsBetween(userId, desde, today);
  const objetivos = await listTargetsBetween(userId, desde, today);
  const pesos = await weightPoints(userId, desde);

  const diasConRegistro = new Set(registros.map((registro) => registro.mealDate));
  const kcalPorDia = new Map<string, number>();
  for (const registro of registros) {
    kcalPorDia.set(registro.mealDate, (kcalPorDia.get(registro.mealDate) ?? 0) + registro.kcal);
  }
  const mediaIngesta =
    kcalPorDia.size === 0
      ? 0
      : [...kcalPorDia.values()].reduce((a, b) => a + b, 0) / kcalPorDia.size;

  const tendencia = weightTrend(pesos);
  const diasDeVentana = Math.max(1, Math.min(CALIBRATION_WINDOW_DAYS, objetivos.length || 1));
  const adherencia = objetivos.length === 0 ? 0 : diasConRegistro.size / objetivos.length;
  const pesosPorSemana = (pesos.length / diasDeVentana) * 7;

  const resultado = calibrateTdee({
    currentTdeeKcal: formulaKcal,
    meanIntakeKcal: Math.round(mediaIngesta),
    // El `?? 0` no es una suposicion escondida: es inalcanzable mientras se
    // cumpla `minWeightsPerWeek`. Con >=5 pesadas por semana en una ventana de
    // 21 dias hay >=15 puntos, y `movingAverage` solo descarta los de los seis
    // primeros dias, asi que siempre quedan >=9 puntos completos, por encima de
    // los 7 que pide `weightTrend`. Si alguna vez se bajara ese minimo, esto
    // pasaria a decir «peso estable» sin haberlo medido, y habria que traer la
    // ausencia de tendencia a `calibrateTdee` como una condicion mas.
    weightTrendKgPerDay: tendencia?.slopeKgPerDay ?? 0,
    daysWithData: diasConRegistro.size,
    adherence: adherencia,
    weightsPerWeek: pesosPorSemana,
    goalChangedInWindow: await huboCambioEnLaVentana(userId, objetivos, desde, today),
  });

  return {
    tdeeKcal: resultado.tdeeKcal,
    source: resultado.source,
    reasonCodes: resultado.reasonCodes,
  };
}

// --------------------------------------------------------------------------
// Tipos de dia
// --------------------------------------------------------------------------

/**
 * Clasifica los siete dias de la semana segun lo que hay PLANIFICADO.
 *
 * `strength_high` sale de `isSignificantLowerBody`, el mismo umbral que usan la
 * recolocacion del cardio y la prescripcion: si aqui se decidiera con otro
 * criterio, la aplicacion diria que el jueves es dia de pierna en una pantalla
 * y que no lo es en otra.
 */
export async function dayTypesOfWeek(userId: string, weekStart: string): Promise<DayType[]> {
  const sesiones = await weekSessions(userId, weekStart);
  const cardio = await listCardioOfWeek(userId, weekStart);

  const tipos: DayType[] = [];
  for (let i = 0; i < 7; i += 1) {
    const dia = addDays(weekStart, i);
    const delDia = sesiones.filter((sesion) => sesion.scheduledDate === dia);
    const fuerza = delDia.filter((sesion) => sesion.sessionKind === 'strength');
    tipos.push(
      dayTypeFor({
        hasStrengthSession: fuerza.length > 0,
        isLowerBodyOrHeavy: fuerza.some((sesion) => isSignificantLowerBody(sesion.volumeByMuscle)),
        hasCardio: cardio.some((carrera) => carrera.scheduledDate === dia),
      }),
    );
  }
  return tipos;
}

/**
 * El turno de entrenamiento de cada dia de la semana.
 *
 * `null` los dias sin sesion de FUERZA: un dia de descanso o de solo cardio no
 * tiene reparto peri-entrenamiento (§17 del encargo), y devolver aqui el turno
 * habitual haria que el motor moviera hidratos por un entrenamiento que no
 * existe.
 *
 * La resolucion sesion/preferencia la hace `resolveTrainingTimeOfDay`, que es
 * el unico sitio donde se leen los dos campos.
 */
export async function trainingTimingOfWeek(
  userId: string,
  weekStart: string,
  habitual: TrainingTimeOfDay,
): Promise<(TrainingTimeOfDay | null)[]> {
  const sesiones = await listSessionsOfDateRange(userId, weekStart, addDays(weekStart, 7));

  const turnos: (TrainingTimeOfDay | null)[] = [];
  for (let i = 0; i < 7; i += 1) {
    const dia = addDays(weekStart, i);
    const fuerza = sesiones.filter(
      (sesion) =>
        sesion.scheduledDate === dia &&
        (sesion.sessionKind === 'strength' || sesion.sessionKind === 'strength_plus_cardio'),
    );
    // Con dos sesiones de fuerza el mismo dia manda la primera del dia: es la
    // que reordena las comidas de alrededor. Es un caso que el plan no genera,
    // pero mover una sesion puede producirlo.
    const primera = fuerza[0];
    turnos.push(
      primera === undefined
        ? null
        : resolveTrainingTimeOfDay(primera.trainingTimeOfDay, habitual),
    );
  }
  return turnos;
}

// --------------------------------------------------------------------------
// Objetivos y menu de la semana
// --------------------------------------------------------------------------

export interface WeekPlanResult {
  weekStart: string;
  targets: NutritionTarget[];
  menu: WeeklyMenu;
  meals: PlannedMeal[];
  reasonCodes: string[];
}

export interface GenerateWeekOptions {
  /**
   * Solo se regeneran los dias a partir de esta fecha; los anteriores se
   * conservan tal cual, con sus mismas filas y su mismo objetivo.
   *
   * Es lo que hace que cambiar el esquema de comidas un miercoles no reescriba
   * el lunes que ya se ha comido (§32 y §33 del encargo). Sin esta opcion, la
   * regeneracion rehace la semana entera, que es lo correcto cuando el usuario
   * pulsa «rehacer el menu» y no cuando lo dispara un cambio de preferencias.
   */
  fromDate?: string;
}

/**
 * Genera —o regenera— los objetivos y el menu de una semana.
 *
 * Los objetivos se escriben ANTES que el menu porque el menu se construye
 * contra ellos: si algo falla a mitad, es preferible tener el objetivo del dia
 * sin menu que un menu sin objetivo que lo justifique.
 */
export async function generateWeekPlan(
  ctx: WriteContext,
  weekStart: string,
  today = todayLocal(),
  opciones: GenerateWeekOptions = {},
): Promise<WeekPlanResult> {
  const comidas = await leerComidas(ctx.userId);
  const slots = validateMealSlots(comidas.slots);

  // `today` es cuando se calcula —edad y ventana de calibracion— y `weekStart`
  // es lo que se planifica. Antes iba solo `today` y cada llamador le daba un
  // significado; ver la cabecera de `nutritionContext`.
  const contexto = await nutritionContext(ctx.userId, today, { forWeekStart: weekStart });
  // El objetivo lo elige el usuario. Sin elegir —o sin saberse todavia, antes
  // de la primera bajada— no se calcula nada: suponer uno seria imponer una
  // dieta, que es justo lo que no se hace (28/09/2026).
  const objetivo = comidas.conocida ? comidas.nutritionGoal : null;
  if (objetivo === null) throw new MissingNutritionDataError('objetivo');

  const energia = energyTargetKcal({
    tdeeKcal: contexto.tdeeKcal,
    rmrKcal: contexto.rmrKcal,
    goal: objetivo,
  });

  const tipos = await dayTypesOfWeek(ctx.userId, weekStart);
  const semana = weeklyTargets({
    dailyMeanKcal: energia.kcal,
    dayTypes: tipos,
    bodyWeightKg: contexto.bodyWeightKg,
    goal: objetivo,
    // El suelo es de cada dia, no de la media: sin el RMR aqui, la modulacion
    // del -10 % de los dias de descanso se lo saltaba (NUTR-001).
    rmrKcal: contexto.rmrKcal,
  });

  const reasonCodes = [...contexto.reasonCodes, ...energia.reasonCodes, ...semana.reasonCodes];

  /*
   * QUE PASA SI EL ESQUEMA DE COMIDAS TODAVIA NO SE SABE (NEW5-002)
   *
   * Se genera igual, con el defecto, y queda DICHO en `reason_codes`. No se
   * bloquea, y la diferencia con el interruptor de la IA es deliberada:
   *
   *   - Bloquear romperia el primer uso sin red. Un dispositivo que nunca ha
   *     bajado nada pero SI tiene perfil y peso es el dispositivo donde se
   *     creo la cuenta, y ahi «no hay fila de preferencias» significa de
   *     verdad «nunca se ha configurado nada». El §5 exige que la aplicacion
   *     funcione entera sin red.
   *   - El da~no, si la suposicion falla, es REPARABLE y el mecanismo ya
   *     existe: en cuanto la preferencia llega y el usuario la toca,
   *     `saveMealPreferences` rehace los menus futuros sin tocar lo comido. En
   *     el caso de la IA no habia reparacion posible: el dinero ya se habia
   *     gastado contra un ajuste explicito.
   *
   * Lo que SI se bloquea es la pantalla que ESCRIBE la preferencia, que es
   * donde estaba el fallo original: pulsar sobre una suposicion escribia lo
   * contrario de lo que se queria.
   */
  if (!comidas.conocida) reasonCodes.push('meal_slots_assumed');

  // Un dia infactible no es un fallo del que se pueda salir aqui —los minimos
  // de proteina y grasa mandan sobre el objetivo calorico— pero tampoco puede
  // pasar en silencio: el menu de ese dia va a recibir unas kcal que sus
  // propios gramos ya se han gastado. El codigo `macros_infeasible` viaja
  // ademas en `reason_codes` de la fila (NUTR-003).
  const infactibles = semana.days.filter((dia) => !dia.feasible);
  if (infactibles.length > 0) {
    logger.warn('engine', 'NUTRITION_TARGET_INFEASIBLE', 'Dias con macros infactibles', {
      weekStart,
      dias: infactibles.map((dia) => ({
        dayType: dia.dayType,
        kcalObjetivo: dia.macros.kcal,
        kcalReales: dia.kcalActual,
      })),
    });
  }

  const desde = opciones.fromDate;
  const objetivos: NutritionTarget[] = [];
  for (const [indice, dia] of semana.days.entries()) {
    const fecha = addDays(weekStart, indice);

    // Un objetivo diario queda CONGELADO el dia que se calcula: el contrato lo
    // dice («una fila por dia») y el «¿Por que?» de mañana se explica contra
    // el. Reescribir el del lunes al cambiar de comidas el miercoles cambiaria
    // el listón contra el que ya se midio lo comido.
    if (desde !== undefined && fecha < desde) {
      const previo = await getTargetForDate(ctx.userId, fecha);
      if (previo !== undefined) {
        objetivos.push(previo);
        continue;
      }
    }

    objetivos.push(
      await saveTarget(ctx, {
        targetDate: fecha,
        dayType: dia.dayType,
        kcal: Math.round(dia.macros.kcal),
        proteinG: dia.macros.proteinG,
        carbsG: dia.macros.carbsG,
        fatG: dia.macros.fatG,
        tdeeEstimate: contexto.tdeeKcal,
        tdeeSource: contexto.tdeeSource,
        rmrEstimate: contexto.rmrKcal,
        reasonCodes: [...reasonCodes, ...dia.reasonCodes],
        decisionId: null,
      }),
    );
  }

  const preferencias = await getPreferences(ctx.userId);
  const favoritos = await favoriteSlugs(ctx.userId);
  const plan = await getPlanOfWeek(ctx.userId, weekStart);
  const ventana = plan?.varietyWindowDays ?? 10;
  const historial = await recipeHistory(ctx.userId, weekStart, ventana);

  const turnos = await trainingTimingOfWeek(
    ctx.userId,
    weekStart,
    comidas.defaultTrainingTimeOfDay,
  );

  // El indice es el dia de la semana, asi que se filtra DESPUES de emparejar
  // cada objetivo con su turno: filtrar antes desplazaria los turnos un dia.
  const peticiones: DayRequest[] = objetivos
    .map((objetivo, indice) => ({
      date: objetivo.targetDate,
      dayType: objetivo.dayType,
      target: {
        kcal: objetivo.kcal,
        proteinG: objetivo.proteinG,
        carbsG: objetivo.carbsG,
        fatG: objetivo.fatG,
      },
      slots,
      trainingTimeOfDay: turnos[indice] ?? null,
    }))
    .filter((peticion) => desde === undefined || peticion.date >= desde);

  const excluidos = [
    ...(preferencias?.dietary.excluded ?? []),
    ...(preferencias?.dietary.allergies ?? []),
    ...(preferencias?.dietary.intolerances ?? []),
  ];

  // Los dias que se conservan no se regeneran, pero SI cuentan para la
  // variedad: sin esto, regenerar de miercoles a domingo podria repetir el
  // martes el plato del lunes.
  const conservadasDeLaSemana =
    desde === undefined
      ? []
      : (await listPlannedMealsBetween(ctx.userId, weekStart, addDays(desde, -1)))
          .filter((comida) => comida.recipeSlug !== null)
          .map((comida) => ({ recipeSlug: comida.recipeSlug as string, usedOn: comida.mealDate }));

  const menu = generateWeeklyMenu(peticiones, {
    bodyWeightKg: contexto.bodyWeightKg,
    varietyWindowDays: ventana,
    favorites: favoritos,
    history: [...historial, ...conservadasDeLaSemana],
    excludedFoodSlugs: excluidos,
  });

  const comidasPlan: PlannedMealInput[] = menu.days.flatMap((dia) =>
    dia.meals.map((comida) => ({
      mealDate: dia.date,
      slot: comida.slot,
      recipeSlug: comida.recipeSlug,
      items: comida.items,
      kcal: comida.macros.kcal,
      proteinG: comida.macros.proteinG,
      carbsG: comida.macros.carbsG,
      fatG: comida.macros.fatG,
    })),
  );

  const guardado = await savePlanWithMeals(ctx, weekStart, comidasPlan, {
    varietyWindowDays: ventana,
    ...(desde === undefined ? {} : { preserveFrom: desde }),
  });

  await markRecipesUsed(
    ctx,
    menu.days.flatMap((dia) =>
      dia.meals.map((comida) => ({ recipeSlug: comida.recipeSlug, usedOn: dia.date })),
    ),
  );

  logger.info('engine', 'MENU_GENERATED', 'Menu semanal generado', {
    weekStart,
    dias: menu.days.length,
    parciales: menu.days.filter((dia) => dia.status === 'partial').length,
    pescado: menu.fishDays,
    legumbre: menu.legumeDays,
  });

  return {
    weekStart,
    targets: objetivos,
    menu,
    meals: guardado.meals,
    reasonCodes: [...reasonCodes, ...menu.reasonCodes],
  };
}

/** Genera el menu de la semana solo si todavia no hay ninguno. */
export async function ensureWeekPlan(
  ctx: WriteContext,
  weekStart: string,
  today = todayLocal(),
): Promise<WeekPlanResult | null> {
  const plan = await getPlanOfWeek(ctx.userId, weekStart);
  if (plan !== undefined) return null;
  return generateWeekPlan(ctx, weekStart, today);
}

// --------------------------------------------------------------------------
// Registro de comidas
// --------------------------------------------------------------------------

const macrosDe = (comida: {
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
}): MacroTargets => ({
  kcal: comida.kcal,
  proteinG: comida.proteinG,
  carbsG: comida.carbsG,
  fatG: comida.fatG,
});

export interface LogMealResult {
  adjusted: PlannedMeal[];
  carryOverKcal: number;
  residualKcal: number;
  closesWithinTolerance: boolean;
  explanation: string;
  reasonCodes: string[];
}

/**
 * Confirma una comida tal y como estaba planificada. **Una pulsacion** (CA-4.2).
 *
 * No hay ninguna pantalla intermedia a proposito: confirmar lo previsto es la
 * accion mas frecuente del dia, y cada paso que se le anada es un dia que el
 * usuario no registra nada.
 */
export async function confirmMeal(
  ctx: WriteContext,
  meal: PlannedMeal,
): Promise<LogMealResult> {
  return registrar(ctx, meal, meal.items, 'confirmed', 'confirmed');
}

/** Registra la comida con otros alimentos o cantidades. */
export async function modifyMeal(
  ctx: WriteContext,
  meal: PlannedMeal,
  items: readonly MealItem[],
): Promise<LogMealResult> {
  return registrar(ctx, meal, items, 'modified', 'modified');
}

/**
 * «Comere fuera»: reserva el hueco con una estimacion editable.
 *
 * Se registra ya, con `entry_mode = 'eating_out'`, para que el resto del dia se
 * reajuste ahora y no cuando el usuario vuelva. Cuando vuelva y anote lo que
 * comio de verdad, `logMeal` reescribe ESA MISMA fila (ADR-038).
 */
export async function declareEatingOut(
  ctx: WriteContext,
  meal: PlannedMeal,
  estimacion: MacroTargets,
): Promise<LogMealResult> {
  return registrar(ctx, meal, [], 'eating_out', 'eating_out', estimacion);
}

/**
 * Saltarse una comida: se registra un cero, no se borra el hueco.
 *
 * Un hueco sin registro es «todavia no ha comido»; un cero es «no va a comer».
 * El reajuste necesita distinguirlos: en el primer caso la comida sigue
 * disponible para redistribuir, en el segundo no.
 */
export async function skipMeal(ctx: WriteContext, meal: PlannedMeal): Promise<LogMealResult> {
  return registrar(ctx, meal, [], 'skipped', 'manual', {
    kcal: 0,
    proteinG: 0,
    carbsG: 0,
    fatG: 0,
  });
}

/**
 * Deshace un registro (D-7).
 *
 * `unlogMeal` existia en el repositorio desde la Fase 4 y **no la llamaba
 * ninguna pantalla**: una comida anotada por error se quedaba anotada para
 * siempre. Salio del mismo barrido que encontro que nadie escribia el peso
 * (R-23): buscar funciones de escritura sin ningun llamador fuera de los tests.
 *
 * Deshace las TRES cosas que hace `registrar`, en orden inverso:
 *
 *   1. La lapida sobre el registro. Borrado suave, como todo lo sincronizable.
 *   2. La comida vuelve a `planned`. Sin esto la tarjeta seguiria diciendo «ya
 *      registrada» sobre un registro que ya no existe.
 *   3. El dia se reajusta otra vez. Ahora esa comida vuelve a estar pendiente,
 *      asi que entra en el reparto en vez de contar como comida.
 *
 * LO QUE NO ES: una maquina del tiempo. El reajuste recalcula con los numeros
 * que hay AHORA, y las otras comidas del dia ya se movieron cuando se registro
 * esta. Deshacer no las devuelve exactamente a donde estaban antes; las vuelve
 * a cuadrar con la comida pendiente de nuevo. Es la misma aritmetica que corre
 * despues de cada registro, no una excepcion, y decirlo aqui evita que alguien
 * cuente con una reversion exacta que no existe.
 */
export async function undoMeal(ctx: WriteContext, meal: PlannedMeal): Promise<LogMealResult> {
  const registros = await listMealLogsOfDay(ctx.userId, meal.mealDate);
  const registro = registros.find((fila) => fila.slot === meal.slot);

  // Sin registro no hay nada que deshacer, y no es un error: el boton puede
  // haberse pulsado dos veces, o la fila puede haberse borrado en otro
  // dispositivo mientras esta pantalla estaba abierta.
  if (registro === undefined) return readjustDay(ctx, meal.mealDate);

  await unlogMeal(ctx, registro);
  await updatePlannedMeal(ctx, meal, { status: 'planned' });

  return readjustDay(ctx, meal.mealDate);
}

async function registrar(
  ctx: WriteContext,
  meal: PlannedMeal,
  items: readonly MealItem[],
  estado: PlannedMeal['status'],
  entryMode: 'confirmed' | 'modified' | 'manual' | 'eating_out',
  macrosForzados?: MacroTargets,
): Promise<LogMealResult> {
  const macros = macrosForzados ?? roundMacros(itemsMacros(items));

  await logMeal(ctx, {
    plannedMealId: meal.id,
    mealDate: meal.mealDate,
    slot: meal.slot,
    items: [...items],
    kcal: macros.kcal,
    proteinG: macros.proteinG,
    carbsG: macros.carbsG,
    fatG: macros.fatG,
    recipeSnapshot: meal.recipeSlug === null ? null : { recipeSlug: meal.recipeSlug },
    entryMode,
  });

  await updatePlannedMeal(ctx, meal, { status: estado });

  return readjustDay(ctx, meal.mealDate);
}

/**
 * Recalcula las comidas que quedan del dia (CA-4.3).
 *
 * Se llama despues de CADA registro, no solo cuando el usuario se desvia: si
 * solo se llamara al desviarse, habria que decidir aqui que cuenta como
 * desviarse, y esa decision ya vive en el motor.
 */
export async function readjustDay(ctx: WriteContext, date: string): Promise<LogMealResult> {
  const objetivo = await getTargetForDate(ctx.userId, date);
  const comidas = await listPlannedMealsOfDay(ctx.userId, date);
  const registros = await listMealLogsOfDay(ctx.userId, date);

  const vacio: LogMealResult = {
    adjusted: [],
    carryOverKcal: 0,
    residualKcal: 0,
    closesWithinTolerance: true,
    explanation: '',
    reasonCodes: [],
  };
  if (objetivo === undefined || comidas.length === 0) return vacio;

  // Solo peso y RMR: el objetivo del día se lee congelado, arriba, así que el
  // gasto y su calibración no cambiarían nada. Pedir el contexto entero leía
  // 21 días de historia después de cada comida registrada (NEW-8).
  const contexto = await contextoBasal(ctx.userId, date);
  const porSlot = new Map(comidas.map((comida) => [comida.slot, comida] as const));

  const logged: LoggedMealInput[] = registros.map((registro) => {
    const planificada = porSlot.get(registro.slot);
    return {
      slot: registro.slot,
      macros: macrosDe(registro),
      plannedMacros: planificada === undefined ? macrosDe(registro) : macrosDe(planificada),
    };
  });

  const registrados = new Set(registros.map((registro) => registro.slot));
  const pendientes = comidas.filter((comida) => !registrados.has(comida.slot));
  if (pendientes.length === 0) return vacio;

  const pending: PendingMealInput[] = pendientes.map((comida) => ({
    slot: comida.slot,
    recipeSlug: comida.recipeSlug,
    items: comida.items,
    plannedMacros: macrosDe(comida),
  }));

  const siguientes = await listTargetsBetween(ctx.userId, addDays(date, 1), addDays(date, 3));

  const resultado = adjustDay({
    dayTarget: {
      kcal: objetivo.kcal,
      proteinG: objetivo.proteinG,
      carbsG: objetivo.carbsG,
      fatG: objetivo.fatG,
    },
    bodyWeightKg: contexto.bodyWeightKg,
    rmrKcal: contexto.rmrKcal,
    logged,
    pending,
    nextDaysKcal: siguientes.map((dia) => dia.kcal),
  });

  const ajustadas: PlannedMeal[] = [];
  for (const ajuste of resultado.meals) {
    const original = porSlot.get(ajuste.slot);
    if (original === undefined) continue;
    ajustadas.push(
      await updatePlannedMeal(ctx, original, {
        items: ajuste.items,
        kcal: ajuste.macros.kcal,
        proteinG: ajuste.macros.proteinG,
        carbsG: ajuste.macros.carbsG,
        fatG: ajuste.macros.fatG,
        adjustedByEngine: ajuste.adjusted,
        adjustmentReason: ajuste.adjustmentReason,
      }),
    );
  }

  return {
    adjusted: ajustadas,
    carryOverKcal: resultado.carryOverKcal,
    residualKcal: resultado.residualKcal,
    closesWithinTolerance: resultado.closesWithinTolerance,
    explanation: resultado.explanation,
    reasonCodes: resultado.reasonCodes,
  };
}

/** Resumen del dia para la pantalla: objetivo, comido y pendiente. */
export interface DaySummary {
  date: string;
  target: NutritionTarget | undefined;
  consumed: MacroTargets;
  planned: MacroTargets;
  meals: PlannedMeal[];
  loggedSlots: Set<MealSlot>;
}

export async function daySummary(userId: string, date: string): Promise<DaySummary> {
  const target = await getTargetForDate(userId, date);
  const meals = await listPlannedMealsOfDay(userId, date);
  const registros = await listMealLogsOfDay(userId, date);

  const suma = (filas: readonly { kcal: number; proteinG: number; carbsG: number; fatG: number }[]) =>
    filas.reduce<MacroTargets>(
      (total, fila) => ({
        kcal: total.kcal + fila.kcal,
        proteinG: total.proteinG + fila.proteinG,
        carbsG: total.carbsG + fila.carbsG,
        fatG: total.fatG + fila.fatG,
      }),
      { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0 },
    );

  return {
    date,
    target,
    consumed: roundMacros(suma(registros)),
    planned: roundMacros(suma(meals)),
    meals,
    loggedSlots: new Set(registros.map((registro) => registro.slot)),
  };
}

/** Objetivo de macros de un dia, en la forma que usa el motor. */
export function targetMacros(target: NutritionTarget): MacroTargets {
  return {
    kcal: target.kcal,
    proteinG: target.proteinG,
    carbsG: target.carbsG,
    fatG: target.fatG,
  };
}


// --------------------------------------------------------------------------
// Cambio del esquema de comidas y del turno de entrenamiento
// --------------------------------------------------------------------------

export interface RegeneratedWeek {
  weekStart: string;
  /** `false` cuando esa semana no tenia menu y por tanto no habia nada que rehacer. */
  regenerated: boolean;
}

/**
 * Semanas cuyo menu se rehace cuando cambia algo que decide COMO se reparte el
 * dia: las comidas elegidas o el turno de entrenamiento.
 *
 * Son la de hoy y la siguiente, y solo si YA tienen menu. Mas alla no hay nada
 * generado —el menu se hace semana a semana— y generar por adelantado seria
 * inventar trabajo.
 */
function semanasARehacer(today: string): string[] {
  const estaSemana = startOfWeek(today);
  return [estaSemana, addDays(estaSemana, 7)];
}

/**
 * Rehace los menus futuros dejando intacto lo ya vivido.
 *
 * EL CORTE ES **HOY**, no «ahora».
 *
 * Ayer se comio con el esquema anterior y su registro lo demuestra: rehacerlo
 * cambiaria un plan contra el que ya se midio lo consumido. Hoy si se rehace,
 * aunque ya se haya desayunado: `readjustDay` recalcula despues lo que queda
 * del dia contando lo registrado, que es exactamente el mecanismo que existe
 * para eso. Lo que NO se toca en ningun caso son los `meal_logs`: lo comido no
 * se reescribe nunca.
 */
export async function regenerateFutureMenus(
  ctx: WriteContext,
  today = todayLocal(),
): Promise<RegeneratedWeek[]> {
  const salida: RegeneratedWeek[] = [];
  for (const semana of semanasARehacer(today)) {
    const plan = await getPlanOfWeek(ctx.userId, semana);
    if (plan === undefined) {
      salida.push({ weekStart: semana, regenerated: false });
      continue;
    }
    // El corte solo aplica a la semana en curso: la siguiente se rehace entera
    // porque ninguno de sus dias ha llegado todavia.
    const desde = semana <= today ? today : semana;
    await generateWeekPlan(ctx, semana, today, { fromDate: desde });
    salida.push({ weekStart: semana, regenerated: true });
  }

  // Lo que queda de HOY se vuelve a cuadrar contando lo que ya se ha comido.
  await readjustDay(ctx, today);
  return salida;
}

export interface MealPreferencesInput {
  slots: readonly MealSlot[];
  defaultTrainingTimeOfDay: TrainingTimeOfDay;
}

/**
 * Guarda el esquema de comidas y rehace lo que deja de valer (§32 del encargo).
 *
 * Las dos cosas van juntas en una funcion, y no en la pantalla, por un motivo
 * concreto: guardar sin regenerar deja una semana de `planned_meals` con las
 * comidas del esquema ANTERIOR presentadas como validas, que es peor que no
 * haber guardado. El usuario veria una merienda que ha quitado, y la lista de
 * la compra seguiria comprando para ella.
 *
 * El dominio valida ANTES de escribir: la pantalla ya impide elegir mal, pero
 * por aqui pasan tambien la importacion y cualquier pantalla futura (§6).
 */
export async function saveMealPreferences(
  ctx: WriteContext,
  input: MealPreferencesInput,
  today = todayLocal(),
): Promise<RegeneratedWeek[]> {
  const slots = validateMealSlots(input.slots);
  await savePreferences(ctx, {
    mealSlots: slots,
    defaultTrainingTimeOfDay: input.defaultTrainingTimeOfDay,
  });
  return regenerateFutureMenus(ctx, today);
}

/**
 * Guarda el objetivo de la alimentacion y rehace los menus futuros.
 *
 * Es el unico sitio que escribe `user_preferences.nutrition_goal` (lo llama
 * `ObjetivoNutricional`). Cambiar de objetivo cambia la energia de cada dia, asi
 * que dejar la semana con el objetivo anterior presentado como valido seria
 * peor que no haber guardado. Lo ya comido no se toca: el corte es hoy.
 */
export async function saveNutritionGoal(
  ctx: WriteContext,
  goal: NutritionGoal,
  today = todayLocal(),
): Promise<RegeneratedWeek[]> {
  await savePreferences(ctx, { nutritionGoal: goal });
  return regenerateFutureMenus(ctx, today);
}

/**
 * Cambia el turno de UNA sesion y rehace el menu que dependia de el (§33).
 *
 * Lo que se preserva y por que: la sesion de ayer no cambia de turno hacia
 * atras —el corte de `regenerateFutureMenus` es hoy— y lo ya registrado se
 * queda como esta. El turno de una sesion pasada se puede corregir, pero
 * corregirlo no reescribe lo que se comio aquel dia; solo queda anotado en la
 * sesion, que es donde se consulta despues.
 */
export async function setSessionTrainingTime(
  ctx: WriteContext,
  session: TrainingSession,
  timing: TrainingTimeOfDay,
  today = todayLocal(),
): Promise<RegeneratedWeek[]> {
  if (session.trainingTimeOfDay === timing) return [];
  // Escribir el turno es cosa del entrenamiento (`trainingRepo`); lo que añade
  // la nutricion es rehacer los menus que dependian de el.
  await escribirTurnoDeSesion(ctx, session, timing);
  // Una sesion pasada no mueve ningun menu: los dias anteriores a hoy no se
  // regeneran nunca.
  if (session.scheduledDate < today) return [];
  return regenerateFutureMenus(ctx, today);
}
